import {
  DEVICE_KIND,
  getDeviceKindLabel,
  parseClientDevices,
} from "../utils/devicePresenceUtils";
import "./DeviceBuildChips.css";

// An iPad shares the phone glyph deliberately: Unicode has no standard tablet
// emoji, and a geometric substitute (`▭`) renders monochrome out-of-family and
// can tofu (#530 review). The kind is carried in visible TEXT instead — the
// title is mouse-only, and that was the original complaint in #529.
const KIND_GLYPH = Object.freeze({
  [DEVICE_KIND.WATCH]: "⌚",
  [DEVICE_KIND.IPHONE]: "📱",
  [DEVICE_KIND.IPAD]: "📱",
  [DEVICE_KIND.UNKNOWN]: "📱",
});

// Only where the glyph cannot carry the distinction. A watch and a phone are
// already unambiguous; an iPad is not.
const KINDS_NEEDING_A_LABEL = new Set([DEVICE_KIND.IPAD]);

const ABSENT = "—";

// Hoisted: `formatDeviceBuildLabel` runs once per chip per render, so a literal
// here would allocate a new RegExp on every call.
const EMBEDDED_BUILD = /\((\d+)\)\s*$/;

/**
 * GMA-TECH/react-app#489 — an absent value must read as absent. Never fall back
 * to a sibling device's build, to a last-known build, or to the string
 * "unknown" the backend persists for devices that never reported one.
 * @param {{ appVersion?: string|null, buildNumber?: string|null }} device
 * @returns {string}
 */
export function formatDeviceBuildLabel(device = {}) {
  const version = device.appVersion || null;
  const build = device.buildNumber || null;
  if (!version && !build) return ABSENT;
  // #529: the presence writer sends the build INSIDE appVersion ("1.0.6(118)"),
  // so appending buildNumber printed it twice on every chip. Collapse only when
  // the embedded value is the SAME build — a disagreement is information, and a
  // non-numeric suffix ("1.0.6(beta)") is not a build at all.
  if (version && build) {
    const embedded = version.match(EMBEDDED_BUILD);
    if (embedded && embedded[1] === String(build)) return version;
  }
  return `${version || ABSENT} (${build || ABSENT})`;
}

/**
 * `lastSeenMs === 0` is a REAL (epoch, very stale) timestamp — devicePresenceUtils
 * preserves it deliberately — so this checks nullish, not falsy. A falsy check
 * reported an epoch-valued record as "never reported" (#489 review).
 */
function formatLastSeen(lastSeenMs, nowMs) {
  if (lastSeenMs === null || lastSeenMs === undefined) return "never reported";
  const minutes = Math.max(0, Math.round((nowMs - lastSeenMs) / 60000));
  if (minutes < 1) return "seen just now";
  if (minutes < 60) return `seen ${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `seen ${hours}h ago`;
  return `seen ${Math.round(hours / 24)}d ago`;
}

/**
 * current | stale | unknown. A device that never reported a timestamp has
 * UNVERIFIABLE freshness, and `stale: false` alone would render its build as a
 * solid current chip — letting QA read an unverifiable historical build as the
 * one running now, which is the defect this component exists to prevent
 * (#489 review).
 */
export function deviceFreshness(device = {}) {
  if (device.lastSeenMs === null || device.lastSeenMs === undefined) {
    return "unknown";
  }
  return device.stale ? "stale" : "current";
}

export function DeviceBuildChip({ device, nowMs }) {
  const freshness = deviceFreshness(device);
  const unverified = freshness !== "current";
  // Two devices of the same kind must be tellable apart, so lead with the
  // reported model where there is one (#489 review).
  const label = device.deviceModel || getDeviceKindLabel(device.kind);
  const seen = formatLastSeen(device.lastSeenMs, nowMs);
  const title = [
    label,
    formatDeviceBuildLabel(device),
    device.osVersion || null,
    seen,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <span
      className={`device-build-chip${unverified ? " stale" : ""}`}
      data-testid={`device-build-chip-${device.deviceId ?? device.kind}`}
      data-kind={device.kind}
      data-stale={device.stale ? "true" : "false"}
      data-freshness={freshness}
      title={title}
      aria-label={title}
    >
      <span
        aria-hidden="true"
        // When rendered through `DeviceBuildChips`, `deviceId` is the devicesMap
        // key that `parseClientDevices` assigns, so it is defined and unique per
        // chip and the `?? kind` arm never runs. `DeviceBuildChip` is exported,
        // though, so a caller outside this file can pass a device without one —
        // hence the fallback, which matches the React key above.
        data-testid={`device-build-chip-glyph-${device.deviceId ?? device.kind}`}
        className="device-build-chip-glyph"
      >
        {KIND_GLYPH[device.kind] || KIND_GLYPH[DEVICE_KIND.UNKNOWN]}
      </span>
      {KINDS_NEEDING_A_LABEL.has(device.kind) && (
        <span className="device-build-chip-kind">
          {getDeviceKindLabel(device.kind)}
        </span>
      )}
      <span className="device-build-chip-value">
        {formatDeviceBuildLabel(device)}
      </span>
      {unverified && (
        <span className="device-build-chip-stale-note">{seen}</span>
      )}
    </span>
  );
}

/**
 * Always-visible per-device app version and build number for the client
 * profile. QA reads "is this client on 117 or 118?" here rather than from
 * production journald (#489).
 *
 * @param {{ devicesMap?: object|null, nowMs?: number }} props
 */
export function DeviceBuildChips({ devicesMap = null, nowMs = Date.now() }) {
  // Deliberately NOT collapsed by kind: collapsing keeps only the newest
  // device per kind, so a client with two iPhones loses one build entirely —
  // which is the opposite of what a per-device build chip is for (#489 review).
  const devices = parseClientDevices(devicesMap, { onlineOnly: false, nowMs });

  if (devices.length === 0) return null;

  return (
    <span className="device-build-chips" data-testid="device-build-chips">
      {devices.map((device) => (
        <DeviceBuildChip
          key={device.deviceId ?? device.kind}
          device={device}
          nowMs={nowMs}
        />
      ))}
    </span>
  );
}

export default DeviceBuildChips;
